import { useMemo } from 'react';
import { GoogleMapView, MapStore } from '../../components/GoogleMapView';
import usePenetrationReportV2Store from '../../stores/penetrationReportV2Store';
import {
  MapPointFilter, MapPointFilterType, MapTimeView, MapTimeViewType, MarketRecord, PointClassification, PointClassificationType,
} from '../../services/penetrationReportV2/types';
import { formatPeriod } from '../../services/penetrationReportV2/periodUtils';

const POINT_FILTER_LABEL: Record<MapPointFilterType, string> = {
  [MapPointFilter.ALL]: 'All',
  [MapPointFilter.CUSTOMERS]: 'Customers',
  [MapPointFilter.NON_CUSTOMERS]: 'Non-customers',
};

function classify(record: MarketRecord): PointClassificationType {
  if (record.isCustomerAsOf && record.isCustomerPrior) return PointClassification.CUSTOMER;
  if (record.isCustomerAsOf && !record.isCustomerPrior) return PointClassification.CONVERTED;
  if (!record.isCustomerAsOf && record.isCustomerPrior) return PointClassification.LOST_TO_NON_CUSTOMER;
  return PointClassification.PROSPECT;
}

function toMapStore(record: MarketRecord, isCustomerForView: boolean): MapStore {
  return {
    id: record.id,
    brand_name: record.brand,
    latitude: record.lat,
    longitude: record.lng,
    channel: record.channel,
    state: record.state,
    customer_id: isCustomerForView ? record.id : null,
    is_customer: isCustomerForView,
  };
}

interface FootprintMapProps {
  records: MarketRecord[];
}

function FootprintMap({ records }: FootprintMapProps) {
  const comparisonEnabled = usePenetrationReportV2Store((s) => s.comparisonEnabled);
  const asOfPeriod = usePenetrationReportV2Store((s) => s.asOfPeriod);
  const comparePeriod = usePenetrationReportV2Store((s) => s.comparePeriod);
  const mapTimeView = usePenetrationReportV2Store((s) => s.mapTimeView);
  const setMapTimeView = usePenetrationReportV2Store((s) => s.setMapTimeView);
  const mapPointFilter = usePenetrationReportV2Store((s) => s.mapPointFilter);
  const setMapPointFilter = usePenetrationReportV2Store((s) => s.setMapPointFilter);

  // The "As of / Prior period / Changes" toggle only makes sense with comparison
  // on; force the "as of" view when comparison is off instead of hiding data.
  const effectiveTimeView = comparisonEnabled ? mapTimeView : MapTimeView.AS_OF;

  const timeViewLabel: Record<MapTimeViewType, string> = {
    [MapTimeView.AS_OF]: formatPeriod(asOfPeriod),
    [MapTimeView.COMPARE]: formatPeriod(comparePeriod),
    [MapTimeView.CHANGES]: 'Changes',
  };

  const { mapPoints, customerCount, nonCustomerCount, convertedCount, lostCount } = useMemo(() => {
    if (effectiveTimeView === MapTimeView.CHANGES) {
      const converted = records.filter((r) => classify(r) === PointClassification.CONVERTED);
      const lost = records.filter((r) => classify(r) === PointClassification.LOST_TO_NON_CUSTOMER);
      return {
        mapPoints: [
          ...converted.map((r) => toMapStore(r, true)),
          ...lost.map((r) => toMapStore(r, false)),
        ],
        customerCount: 0,
        nonCustomerCount: 0,
        convertedCount: converted.length,
        lostCount: lost.length,
      };
    }

    const isCustomerField: keyof MarketRecord = effectiveTimeView === MapTimeView.COMPARE ? 'isCustomerPrior' : 'isCustomerAsOf';
    const filtered = records.filter((r) => {
      if (mapPointFilter === MapPointFilter.CUSTOMERS) return r[isCustomerField];
      if (mapPointFilter === MapPointFilter.NON_CUSTOMERS) return !r[isCustomerField];
      return true;
    });

    return {
      mapPoints: filtered.map((r) => toMapStore(r, Boolean(r[isCustomerField]))),
      customerCount: records.filter((r) => r[isCustomerField]).length,
      nonCustomerCount: records.filter((r) => !r[isCustomerField]).length,
      convertedCount: 0,
      lostCount: 0,
    };
  }, [records, effectiveTimeView, mapPointFilter]);

  return (
    <aside className="pr2-card pr2-mapcard">
      <div className="pr2-heading">
        <div>
          <h2>Market footprint</h2>
          <p>Customer coverage and remaining market</p>
        </div>
      </div>

      {comparisonEnabled && (
        <div className="pr2-maptime">
          {(Object.values(MapTimeView) as MapTimeViewType[]).map((view) => (
            <button
              type="button"
              key={view}
              className={view === mapTimeView ? 'active' : ''}
              onClick={() => setMapTimeView(view)}
            >
              {timeViewLabel[view]}
            </button>
          ))}
        </div>
      )}

      {effectiveTimeView !== MapTimeView.CHANGES && (
        <div className="pr2-mapfilter">
          {(Object.values(MapPointFilter) as MapPointFilterType[]).map((filter) => (
            <button
              type="button"
              key={filter}
              className={filter === mapPointFilter ? 'active' : ''}
              onClick={() => setMapPointFilter(filter)}
            >
              {POINT_FILTER_LABEL[filter]}
            </button>
          ))}
        </div>
      )}

      <div className="pr2-map">
        <GoogleMapView stores={mapPoints} height="250px" fitBounds zoom={4} />
      </div>

      <div className="pr2-maplegend">
        {effectiveTimeView === MapTimeView.CHANGES ? (
          <>
            <span><i className="pr2-dot" />Converted <strong>{convertedCount.toLocaleString()}</strong></span>
            <span><i className="pr2-dot red" />Moved to non-customer <strong>{lostCount.toLocaleString()}</strong></span>
          </>
        ) : (
          <>
            <span><i className="pr2-dot" />Customers <strong>{customerCount.toLocaleString()}</strong></span>
            <span><i className="pr2-dot red" />Non-customers <strong>{nonCustomerCount.toLocaleString()}</strong></span>
          </>
        )}
      </div>
      <p className="pr2-note">Marker positions are illustrative seed data, not live geocoding.</p>
    </aside>
  );
}

export default FootprintMap;
